import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import type { SchoolCardProps, SchoolData } from './types';
import './SchoolCard.css';

// Configure DOMPurify with strict allowlist
const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'strong', 'em', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: [],
  });
};

// ─── Icons ────────────────────────────────────────────────────
const ArrowIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const BookOpenIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

const UsersIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const MailIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const FacebookIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

// Helper to convert country code to flag emoji
function getFlagEmoji(countryCode: string): string {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

// ─── Lightbox Component ───────────────────────────────────────
interface LightboxProps {
  images: string[];
  initialIndex: number;
  schoolName: string;
  onClose: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ images, initialIndex, schoolName, onClose }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, handlePrev, handleNext]);

  return (
    <div className="sch-lightbox-overlay" onClick={onClose}>
      <div className="sch-lightbox-content" onClick={(e) => e.stopPropagation()}>
        <button className="sch-lightbox-close" onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon />
        </button>

        <div className="sch-lightbox-image-container">
          <img src={images[currentIndex]} alt={`${schoolName} - ${t('schoolCard.photoAlt', { index: currentIndex + 1 })}`} />
        </div>

        {images.length > 1 && (
          <>
            <button className="sch-lightbox-nav sch-lightbox-prev" onClick={handlePrev} aria-label={t('common.previous')}>
              <ChevronLeftIcon />
            </button>
            <button className="sch-lightbox-nav sch-lightbox-next" onClick={handleNext} aria-label={t('common.next')}>
              <ChevronRightIcon />
            </button>
            <div className="sch-lightbox-counter">
              {currentIndex + 1} / {images.length}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Detail Modal ─────────────────────────────────────────────
interface SchoolDetailModalProps {
  school: SchoolData;
  onClose: () => void;
}

const SchoolDetailModal: React.FC<SchoolDetailModalProps> = ({ school, onClose }) => {
  const { t } = useTranslation();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const allPhotos = school.photoUrls?.length > 0
    ? school.photoUrls
    : school.photoUrl
      ? [school.photoUrl]
      : [];

  const displayPhotos = allPhotos.slice(0, 5);
  const remainingPhotos = allPhotos.length - 5;

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const location = [school.city, school.country].filter(Boolean).join(', ');

  return (
    <div className="sch-modal-backdrop" onClick={onClose}>
      <div className="sch-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="sch-modal-close" onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon />
        </button>

        {/* Gallery */}
        {displayPhotos.length > 0 && (
          <div className="sch-modal-gallery">
            <div className="sch-modal-gallery-main" onClick={() => openLightbox(0)}>
              <img src={displayPhotos[0]} alt={school.name} />
              <div className="sch-modal-gallery-overlay">
                <span>{t('schoolCard.viewGallery')}</span>
              </div>
            </div>
            {displayPhotos.length > 1 && (
              <div className="sch-modal-gallery-thumbs">
                {displayPhotos.slice(1).map((photo, index) => (
                  <div
                    key={index}
                    className="sch-modal-gallery-thumb"
                    onClick={() => openLightbox(index + 1)}
                  >
                    <img src={photo} alt={`${school.name} - ${index + 2}`} />
                    {index === 3 && remainingPhotos > 0 && (
                      <div className="sch-modal-gallery-more">+{remainingPhotos}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="sch-modal-body">
          {/* Header */}
          <div className="sch-modal-header">
            <h2 className="sch-modal-name">{school.name}</h2>
            {location && (
              <p className="sch-modal-location">
                <MapPinIcon /> {location}
                {school.countryCode && (
                  <span className="sch-modal-flag">{getFlagEmoji(school.countryCode)}</span>
                )}
              </p>
            )}
            {school.address && <p className="sch-modal-address">{school.address}</p>}
          </div>

          {/* Description */}
          {school.description && (
            <div
              className="sch-modal-description"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(school.description) }}
            />
          )}

          {/* Highlights */}
          {school.highlights?.length > 0 && (
            <div className="sch-modal-section">
              <h3 className="sch-modal-section-title">{t('schoolCard.highlights')}</h3>
              <ul className="sch-modal-highlights">
                {school.highlights.map((h, i) => (
                  <li key={i}><CheckCircleIcon /> {h}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Amenities */}
          {school.amenities?.length > 0 && (
            <div className="sch-modal-section">
              <h3 className="sch-modal-section-title">{t('schoolCard.amenities')}</h3>
              <div className="sch-modal-amenities">
                {school.amenities.map((a, i) => (
                  <span key={i} className="sch-modal-amenity">{a}</span>
                ))}
              </div>
            </div>
          )}

          {/* Nationality Mix */}
          {school.nationalityMix && school.nationalityMix.length > 0 && (
            <div className="sch-modal-section">
              <h3 className="sch-modal-section-title">
                <UsersIcon /> {t('schoolCard.nationalityMix')}
              </h3>
              <div className="sch-modal-nationalities">
                {school.nationalityMix.map((n, i) => (
                  <div key={i} className="sch-modal-nat-row">
                    <span className="sch-modal-nat-label">{n.label}</span>
                    <div className="sch-modal-nat-bar">
                      <div
                        className="sch-modal-nat-fill"
                        style={{ width: `${Math.min(n.percentage, 100)}%` }}
                      />
                    </div>
                    <span className="sch-modal-nat-pct">{n.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Courses */}
          {school.courses?.length > 0 && (
            <div className="sch-modal-section">
              <h3 className="sch-modal-section-title">
                <BookOpenIcon /> {t('schoolCard.courses')}
                <span className="sch-modal-courses-count">({school.courses.length})</span>
              </h3>
              <div className="sch-modal-courses">
                {school.courses.slice(0, 5).map((c) => (
                  <div key={c.offeringId} className="sch-modal-course">
                    <span className="sch-modal-course-name">{c.name}</span>
                    {c.category && <span className="sch-modal-course-cat">{c.category}</span>}
                  </div>
                ))}
                {school.courses.length > 5 && (
                  <div className="sch-modal-courses-more">
                    {t('schoolCard.andMore', { count: school.courses.length - 5 })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contact */}
          {(school.website || school.phone || school.email || school.facebook || school.instagram) && (
            <div className="sch-modal-section sch-modal-contact-section">
              {school.website && (
                <a href={school.website} target="_blank" rel="noopener noreferrer" className="sch-modal-website-btn">
                  <GlobeIcon /> {t('schoolCard.visitWebsite')}
                </a>
              )}
              <div className="sch-modal-contact-row">
                {school.phone && (
                  <a href={`tel:${school.phone}`} className="sch-modal-contact-icon" title={school.phone}>
                    <PhoneIcon />
                  </a>
                )}
                {school.email && (
                  <a href={`mailto:${school.email}`} className="sch-modal-contact-icon" title={school.email}>
                    <MailIcon />
                  </a>
                )}
                {school.facebook && (
                  <a href={school.facebook} target="_blank" rel="noopener noreferrer" className="sch-modal-contact-icon" title="Facebook">
                    <FacebookIcon />
                  </a>
                )}
                {school.instagram && (
                  <a href={school.instagram} target="_blank" rel="noopener noreferrer" className="sch-modal-contact-icon" title="Instagram">
                    <InstagramIcon />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && allPhotos.length > 0 && (
        <Lightbox
          images={allPhotos}
          initialIndex={lightboxIndex}
          schoolName={school.name}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
};

// ─── School Icon ──────────────────────────────────────────────
const SchoolIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c3 3 9 3 12 0v-5" />
  </svg>
);

// ─── Preview Card ─────────────────────────────────────────────
interface SchoolPreviewCardProps {
  school: SchoolData;
  onSelect: (school: SchoolData) => void;
}

const SchoolPreviewCard: React.FC<SchoolPreviewCardProps> = ({ school, onSelect }) => {
  const { t } = useTranslation();
  const [logoError, setLogoError] = useState(false);

  const location = [school.city, school.country].filter(Boolean).join(', ');
  const highlight = school.highlights?.[0];
  const coursesCount = school.courses?.length || 0;
  const amenitiesPreview = school.amenities?.slice(0, 3) || [];

  return (
    <article className="sch-card" onClick={() => onSelect(school)}>
      {/* Logo on left */}
      <div className="sch-card-logo">
        {school.photoUrl && !logoError ? (
          <img
            src={school.photoUrl}
            alt={school.name}
            onError={() => setLogoError(true)}
          />
        ) : (
          <div className="sch-card-logo-placeholder">
            <SchoolIcon />
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="sch-card-main">
        {/* Header with location */}
        <div className="sch-card-header">
          <span className="sch-card-location">
            {school.countryCode && <span className="sch-card-flag">{getFlagEmoji(school.countryCode)}</span>}
            {location}
          </span>
          {coursesCount > 0 && (
            <span className="sch-card-courses-badge">
              <BookOpenIcon /> {coursesCount} {t('schoolCard.coursesAvailable')}
            </span>
          )}
        </div>

        {/* Body */}
        <div className="sch-card-body">
          <h3 className="sch-card-name">{school.name}</h3>
          {highlight && <p className="sch-card-highlight">"{highlight}"</p>}

          {/* Amenities preview */}
          {amenitiesPreview.length > 0 && (
            <div className="sch-card-amenities">
              {amenitiesPreview.map((a, i) => (
                <span key={i} className="sch-card-amenity">{a}</span>
              ))}
              {school.amenities && school.amenities.length > 3 && (
                <span className="sch-card-amenity-more">+{school.amenities.length - 3}</span>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sch-card-footer">
          <span className="sch-card-cta">{t('schoolCard.explore')} <ArrowIcon /></span>
        </div>
      </div>
    </article>
  );
};

// ─── Main Component ───────────────────────────────────────────
export const SchoolCard: React.FC<SchoolCardProps> = ({ data, portalContainer }) => {
  const { t } = useTranslation();
  const [selectedSchool, setSelectedSchool] = useState<SchoolData | null>(null);

  const schools = Array.isArray(data) ? data : [data];

  return (
    <div className="card-section-wrapper">
      <div className="card-section-header">
        <div className="card-section-title-row">
          <h2 className="card-section-title">{t('schoolCard.schools')}</h2>
          <span className="card-section-count">{schools.length}</span>
        </div>
      </div>
      <div className="card-stack">
        {schools.map((school, index) => (
          <SchoolPreviewCard
            key={school.sessionSchoolId || school.schoolId || index}
            school={school}
            onSelect={setSelectedSchool}
          />
        ))}
      </div>

      {selectedSchool && (
        portalContainer
          ? createPortal(
              <SchoolDetailModal school={selectedSchool} onClose={() => setSelectedSchool(null)} />,
              portalContainer
            )
          : <SchoolDetailModal school={selectedSchool} onClose={() => setSelectedSchool(null)} />
      )}
    </div>
  );
};
